import React, { useState, useEffect, useMemo } from 'react'
import { useLazyQuery } from '@apollo/client'
import styled from 'styled-components'

import { GET_ITEM_FAMILY_DETAILS, GET_ITEM_FAMILY_PRECUSTOMIZED_ITEMS } from 'setup/gqlQueries/gqlItemQueries'
import { GET_ITEM_PRICE } from 'setup/providerGQL'
import TechSpecs from './TechSpecs'
import { useDebounceValue } from 'pageComponents/_common/helpers/generalHelperFunctions'
import { DivSection } from './ItemDetailsBase'
import Accessories from './Accessories'
import RelatedTo from './RelatedTo'
import SubstituteLink from 'pageComponents/_common/SubstituteLink'
import ItemLinks from './ItemLinks'
import NumberFormat from 'react-number-format'
import { FAMILY_MEMBER_KIND } from 'pageComponents/_common/constants/customizationConstants'
import { DetailButtonSmall } from 'pageComponents/Account/orderQuoteShared'
import PrecustomizedItem from './PrecustomizedItem'
import CTLAXIS from '../../../imgs/airline/ctl-axis.png'

const PItemExtendedDescription = styled.p`
	font-size: 16px;
`

const ItemCustomizerDiv = styled.div`
flex: 1;
padding: 0 16px;
max-width: 100%;
`

const CutToLengthSection = styled.section`
label {
    display: block;
}
input[type='radio'] {
    margin-right: 10px;
}
margin-top: 10px;

`
const LengthEntryDiv = styled.div`
margin-left: 20px;
input {
    margin-bottom: 1rem;
}
`
const CutToLengthInfo = styled.div`
font-size: .95em;
p{
    font-weight: bold;
    margin-bottom: .5rem;
}
`
const MaxLengthDiv = styled.div`
background-color: #FFFCE5;
padding: 10px 15px;
margin-bottom: 10px;
p {
    margin: 0;
    padding: 0;
}
`

const MachiningTableContainer = styled.div`
    overflow: auto;
`

const MachiningTable = styled.table`
    border: 1px solid #CCC;
    border-collapse: collapse;
    background-color: #F9F9FC;
    margin-bottom: 10px;
    overflow-x: auto;
    
    input {
        margin-bottom: 0;
    }
    
    th, td {
        font-size: 12px;
        border: 1px solid #CCC;
        white-space: nowrap;
    }
    
    th {
        padding: 10px;
    }

    td {
        padding: 5px 10px;
    }

    .precustom-add {
        text-align: center;
        min-width: 150px;
        font-size: 15px;
    }
`

const SubDiv = styled.div`
    display: flex;
`

const MachiningDiv = styled.div`
    display:inline;

    p {
        margin-bottom: 0;
    }
`

const CTLImage = styled.img`
    display:inline;
    position: relative;
    bottom: 50px;
    margin-left: 10px;
`

export default function ItemDetailsCustomizer(props) {
    const { 
        itemDetails, 
        customItemType, 
        setCustomItemType,
        unitPrice,
        quantity,
        customizerItem,
        setCustomizerItem,
        setCustomizerPrice,
        itemAvailability,
        setItemAvailability,
        handleItemChange,
        familyAccessoryItems,
        accessoryItems,
        accessoryItemsInfo,
        accessoryItemPrices,
        setShowAddedToCartModal,
        invMastUid,
        handleAddToCart,
    } = props
    
    // Assuming only one item family per item for now
    const itemFamily = itemDetails?.itemFamilies[0]

    const [cutToLength, setCutToLength] = useState({})
    const [customizationItemPrices, setCustomizationItemPrices] = useState([])
    const [familyItemPrices, setFamilyItemPrices] = useState([])
    const [precustomItemPrices, setPrecustomItemPrices] = useState([])
    const [length, setLength] = useState(150.0)
    const debouncedLength = useDebounceValue(length)
    const [precustomizedItems, setPrecustomizedItems] = useState([])
    const [machiningActiveRow, setMachiningActiveRow] = useState()


    const customizerLength = useMemo(() => customizerItem.customizations?.find(c => c.customizationKind === "CutToLength")?.dimLengthX ?? 150.0, [customizerItem])
    const cutToLengthMaterial = useMemo(() => itemFamily?.members?.find(m => m.relationshipToFamily === "CutToLengthMaterial") ?? {}, [itemFamily])

    const [getFamilyInfo, { data: familyInfo }] = useLazyQuery(GET_ITEM_FAMILY_DETAILS, {
        variables: { familyHeadInvMastUid: itemFamily?.familyInvMastUid, memberInvMastUid: itemDetails?.invMastUid },
        fetchPolicy: 'no-cache',
        onCompleted: result => {
            const customizations = result.itemFamilyDetails?.customizerOptions
            setCutToLength(customizations?.find(c => c.kind === "CutToLength"))

            //Build the price request objects
            const customizationItemPriceRequests = customizations?.map(i => {
                return {
                    invMastUid: i.customizationInvMastUid,
                    quantity: quantity
                }
            })

            const familyItemPriceRequests = itemFamily.members.map(i => {
                return {
                    invMastUid: i.invMastUid,
                    quantity: quantity
                }
            })

            queryCustomizationItemPrices({
                variables: {
                    items: customizationItemPriceRequests
                }
            })

            queryFamilyItemPrices({
                variables: {
                    items: familyItemPriceRequests
                }
            })
        },
    })

    const [getFamilyPrecustomizedItems, { data: familyPrecustomizedItems}] = useLazyQuery(GET_ITEM_FAMILY_PRECUSTOMIZED_ITEMS, {
        variables: { familyHeadInvMastUid: itemFamily?.familyInvMastUid },
        fetchPolicy: 'no-cache',
        onCompleted: result => {
            if(result.itemFamilyPrecustomizedItemsContainer) {
                setPrecustomizedItems(result.itemFamilyPrecustomizedItemsContainer.precustomizedItems)
                const precustomItemPriceRequests = result.itemFamilyPrecustomizedItemsContainer.precustomizedItems.flatMap(i => {
                        return i.precustomizedItemCustomizations.map(p => p.customizationInvMastUid)
                })

                const uniquePrecustomItemPriceRequests = [...new Set(precustomItemPriceRequests)].map(i => {
                    return {
                        invMastUid: i
                    }
                })
                
                queryPrecustomizedItemPrices({
                    variables: {
                        items: uniquePrecustomItemPriceRequests
                    }
                })
            }
            
        }
    })

    const [queryCustomizationItemPrices] = useLazyQuery(GET_ITEM_PRICE, {
        onCompleted: data => {
            setCustomizationItemPrices(data.getItemPrices)
        }
    })

    const [queryFamilyItemPrices] = useLazyQuery(GET_ITEM_PRICE, {
        onCompleted: data => {
            setFamilyItemPrices(data.getItemPrices)
        }
    })

    const [queryPrecustomizedItemPrices] = useLazyQuery(GET_ITEM_PRICE, {
        onCompleted: data => {
            setPrecustomItemPrices(data.getItemPrices)
        }
    })

    useEffect(() => {
            getFamilyInfo()
            if(itemFamily?.precustomizedItems) {
                getFamilyPrecustomizedItems()
            }
    }, [getFamilyInfo, customItemType])

    const SpareParts = familyAccessoryItems.concat(accessoryItems)?.filter(a => a.type === 1)
    const AccessoryItems = familyAccessoryItems.concat(accessoryItems)?.filter(a => a.type === 2)
    const SubstituteItems = familyAccessoryItems.concat(accessoryItems)?.filter(a => a.type === 3)

    useEffect(() => {
        const customizationPricesByInvMastUid = Object.fromEntries(customizationItemPrices.concat(precustomItemPrices).map(i => ([i.invMastUid, i.unitPrice])))

        const customizationsTotalPrice = customizerItem?.customizations?.reduce((sum, next) =>
            sum + customizationPricesByInvMastUid[next.customizedItem.invMastUid], 0) ?? 0

        if (customizerLength >= cutToLength?.minimumLengthX && customizerLength <= cutToLength?.maximumLengthX) {
            if (customizerLength >= cutToLength?.minimumFullChargeLength && cutToLength?.minimumFullChargeLength != null) {
                setCustomizerPrice(familyItemPrices.find(i => i.invMastUid === itemFamily?.members?.find(m => m.relationshipToFamily === "Stick")?.invMastUid)?.unitPrice
                + customizationsTotalPrice)
            }
            else {
                setCustomizerPrice((Math.ceil(customizerLength) * unitPrice) + customizationsTotalPrice)        
            }
        }
        else {
            setCustomizerPrice(null)
        }
    }, [customizerLength, customizerItem, customizationItemPrices, cutToLength, itemFamily?.members, unitPrice, setCustomizerPrice, familyItemPrices])

    useEffect(() => {
        // TODO: Figure out a way to load customization options for Family Head item.
        if(itemFamily?.familyInvMastUid === itemDetails?.invMastUid) {
            handleItemChange(cutToLengthMaterial?.invMastUid)
        }
    }, [handleItemChange, itemDetails?.invMastUid, itemFamily, setCustomItemType]) 

    useEffect(() => {
        setCustomizerItem(
            { 
                invMastUid: itemFamily?.familyInvMastUid,
                quantity: parseInt(quantity, 10),
                itemUnitPriceOverride: null,
                customerPartNumberId: 0,
                customizations: [{
                    axis: "X",
                    dimLengthX: parseFloat(debouncedLength),
                    materialItemInvMastUid: cutToLengthMaterial?.invMastUid,
                    unitOfMeasure: familyInfo?.itemFamilyDetails?.unitOfMeasure,
                    customizationKind: "CutToLength",
                    customizedItem: {
                        invMastUid: cutToLength?.customizationInvMastUid,
                    }
                }]
            }
            )
    }, [cutToLength, debouncedLength, quantity, itemFamily, familyInfo?.itemFamilyDetails?.unitOfMeasure, setCustomizerItem])

    useEffect(() => {
        if((customItemType === 'cut' || customItemType === 'machining') && cutToLength && itemDetails) {
            const calculatedAvailability = Math.floor(parseInt(itemDetails?.availability) / parseInt(cutToLength?.maximumLengthX))
            setItemAvailability({...itemAvailability, availability: `${calculatedAvailability} Stick(s) Available`})
        }
    }, [customItemType, itemDetails, cutToLength])

    return (
        <ItemCustomizerDiv>
            <PItemExtendedDescription dangerouslySetInnerHTML={{ __html: itemDetails?.extendedDesc }} />
<SubDiv>{SubstituteItems?.length > 0 && <SubstituteLink className='scrollToLink' href='#sub' />}</SubDiv>
            { cutToLength && 
            <CutToLengthSection>
                <h2>Options</h2>
                <div>
                {/* Temporary Fix to allow partial family members */}
                { itemFamily?.members.some(m => m.relationshipToFamily === FAMILY_MEMBER_KIND.Bundle) && <label><input type='radio' value='bundle' name='itemoptions' checked={familyInfo?.itemFamilyDetails?.invMastUid === itemFamily?.members.find(m => m.relationshipToFamily === "Bundle").invMastUid} onChange={() => { setCustomItemType('bundle'); handleItemChange(itemFamily?.members.find(m => m.relationshipToFamily === "Bundle")?.invMastUid)}} />Bundle ({itemFamily?.members.find(m => m.relationshipToFamily === "Bundle").relationshipQty} pieces at {cutToLength.maximumLengthX}{String(cutToLength.unitOfMeasure).toLowerCase()})</label> }
                { itemFamily?.members.some(m => m.relationshipToFamily === FAMILY_MEMBER_KIND.Stick) && <label><input type='radio' value='stick' name='itemoptions' checked={familyInfo?.itemFamilyDetails?.invMastUid === itemFamily?.members.find(m => m.relationshipToFamily === "Stick").invMastUid} onChange={() => { setCustomItemType('stick'); handleItemChange(itemFamily?.members.find(m => m.relationshipToFamily === "Stick")?.invMastUid)}} />Full Stick ({cutToLength.maximumLengthX}{ String(cutToLength.unitOfMeasure).toLowerCase()})</label> }
                { itemFamily?.members.some(m => m.relationshipToFamily === FAMILY_MEMBER_KIND.CutToLengthMaterial) && <label><input type='radio' value='cut' name='itemoptions' checked={familyInfo?.itemFamilyDetails?.invMastUid === itemFamily?.members.find(m => m.relationshipToFamily === "CutToLengthMaterial").invMastUid && customItemType === 'cut'} onChange={() => { setCustomItemType('cut'); handleItemChange(itemFamily?.familyInvMastUid)}} />Cut To Length (Millimeters)</label> }
                { customItemType === 'cut' && 
                <LengthEntryDiv>
                    <NumberFormat decimalScale={1} type='text' step="0.1" min={ cutToLength.minimumLengthX } max={ cutToLength.maximumLengthX } value={length??cutToLength.minimumLengthX} onChange={ e => setLength(e.target.value)} />
                </LengthEntryDiv>
                }
                { itemFamily?.precustomizedItems.length > 0 && <label><input type='radio'value='machining' name='itemoptions' checked={customItemType === 'machining'} onChange={() => { setCustomItemType('machining'); handleItemChange(itemFamily?.familyInvMastUid) }}/>Cut To Length Machining Options</label>}
                </div>
                
                { (customItemType === 'cut' || customItemType === 'machining') && 
                <LengthEntryDiv>
                    
                    {customItemType === 'machining' &&
                        <>
                        <h5>Machining Options</h5>
                        <SubDiv>
                            <MachiningDiv>
                                <p>Please enter a length and quantity for an item below.</p>
                                <p>Please contact us for more customized machining. </p>
                            </MachiningDiv>
                            <CTLImage src={CTLAXIS} />
                        </SubDiv>
                        <MachiningTableContainer>
                            <MachiningTable>
                                
                                <thead>
                                    <tr>
                                        <th>Profile</th>
                                        <th>Machining</th>
                                        <th>Length (mm)</th>
                                        <th>Quantity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {precustomizedItems.map((item, idx) => {
                                        return (
                                            <PrecustomizedItem key={idx} {...{item, cutToLength, cutToLengthMaterial, setCustomizerItem, isActive: idx===machiningActiveRow, setActive: () => setMachiningActiveRow(idx), handleAddToCart}} />
                                        )
                                    })}
                                </tbody>
                            </MachiningTable>
                        </MachiningTableContainer>
                        </>
                    }
                    {customizerLength > cutToLength.maximumLengthX && <MaxLengthDiv>
                        <p>For lengths over { cutToLength.maximumLengthX }{String(cutToLength.unitOfMeasure).toLowerCase()} please call us  at <a href='tel:+18009997378'>800-999-7378</a> for pricing or learn more about our <a target="_blank" href='/pages/services/engineered-systems-and-assemblies/structural-framing-systems'>engineered framing solutions.</a></p>
                    </MaxLengthDiv>}
                    
                    {customizerLength < cutToLength.minimumLengthX && <MaxLengthDiv>
                        <p>For lengths under { cutToLength.minimumLengthX }{String(cutToLength.unitOfMeasure).toLowerCase()} please <a href='tel:+18009997378'>call us at <a href='tel:+18009997378'></a>800-999-7378</a> for pricing or learn more about our <a target="_blank" href='/pages/services/engineered-systems-and-assemblies/structural-framing-systems'>engineered framing solutions.</a></p>
                    </MaxLengthDiv>}
                    <CutToLengthInfo>
                        {customizerLength >= 2641 && <p>All lengths 2,387mm - 2,641mm may incur over-length fees via UPS and FedEx.<br />All lengths over 2,641mm will be shipped via freight.</p>}
                        <p>Cutting & Packaging Included. <a href='https://www.youtube.com/watch?v=msIBFiahdzk' target='_blank'>View our packaging.</a></p>
                        <p>Order may require up to 2 business days of processing time.</p>
                        <p>Minimum: { cutToLength.minimumLengthX }{String(cutToLength.unitOfMeasure).toLowerCase()} Maximum: { cutToLength.maximumLengthX }{String(cutToLength.unitOfMeasure).toLowerCase()}.</p>
                        <p>Standard cut tolerance is +/- .5mm. Please <a href='/pages/contact/framing-request' target='_blank'>contact us</a> for tighter tolerance needs.</p>
                    </CutToLengthInfo>
                </LengthEntryDiv> }
            </CutToLengthSection>}
            <hr />
            {!!itemDetails?.itemTechSpecs?.length && ( <TechSpecs {...{ itemDetails }} /> )}

            {itemDetails?.itemLinks?.length > 0 && 
			<section>
				<h2 id="links">Links</h2>
				<ItemLinks links={itemDetails?.itemLinks} />
			</section>}
            {SpareParts?.length > 0 && 
				<section>
					<h2 id='spare'>Spare Parts</h2>
					<Accessories 
						items={SpareParts}
						info={accessoryItemsInfo}
						prices={accessoryItemPrices} 
						setShowAddedToCartModal={setShowAddedToCartModal}
					/>
				</section>
			}

			{AccessoryItems?.length > 0 && 
				<section>
					<h2 id='accessory'>Accessory Items</h2>
					<Accessories 
						items={AccessoryItems}
						info={accessoryItemsInfo}
						prices={accessoryItemPrices} 
						setShowAddedToCartModal={setShowAddedToCartModal}
					/>
				</section>
			}

			{SubstituteItems?.length > 0 && 
				<section>
					<h2 id='sub'>Substitute Items</h2> 
					<Accessories 
						items={SubstituteItems}
						info={accessoryItemsInfo}
						prices={accessoryItemPrices} 
						setShowAddedToCartModal={setShowAddedToCartModal}
					/>
				</section>
			}
			
			{itemDetails?.hasRelations && (
				<section>
					<h2 id='relatedto'>Related To</h2>
					<RelatedTo
						{...{ invMastUid }}
					/>
				</section>
			)}
        </ItemCustomizerDiv>
        
    )
}